<template>
  <bleed-layout v-if="contact">
    <div class="grid-x viewContactComponent" :style="`min-height: ${contactViewHeight || 0}px`">

      <!--Left Column-->
      <div id="contactLeftColumn" class="cell large-3 align-self-stretch bg-white">

        <!--Top Bar-->
        <div class="grid-x align-justify align-middle border-bottom bg-white leftTopBar">

          <!--Back Link-->
          <div class="cell shrink" v-if="backLinkType === 'search'">
            <a class="button clear backButton" @click="goBack()">
              <span class="fas fa-chevron-left"></span> Back to Search
            </a>
          </div>

          <!--Navigate List-->
          <div class="cell auto" v-if="contactList">
            <list-navigator
              :activeContact="contact"
              :contactList="contactList"
              :contacts="contactListContacts ? contactListContacts.contacts : null"
              show-back-link
              @goToContact="goToContact($event)"
              @goBackToList="goBackToList()"></list-navigator>
          </div>
        </div>
        <!--/Top Bar-->

        <!--Content-->
        <div class="columnContentWrapper">

          <!--Name-->
          <section class="margin-top text-center" style="position: relative">

            <span style="position: absolute; left: 0; top: 0;">
              <priority-selector :value="contact.priority" @change="handleUpdatePriority($event)"></priority-selector>
            </span>

            <!--Avatar-->
            <div class="margin-bottom">
              <span class="avatar-wrapper">
                <a @click="handleImageClick" v-if="contact.images.length > 0">
                  <contact-avatar :contact="contact" square :size="100" />
                </a>
                <contact-avatar :contact="contact" square :size="100" v-else />
                <span class="contact-score label" v-if="contact.score !== null">{{ contact.score }}</span>
              </span>
            </div>
            <!--/Avatar-->

            <!--Name-->
            <div class="contactName">
              <template v-if="contact.is_organization">
                <h4 class="text-bold">
                  <salutation-name :contact="contact"></salutation-name>
                </h4>
                <div v-if="contact.first_name || contact.last_name">
                  <salutation-name organization-contact :contact="contact"></salutation-name>
                </div>
              </template>
              <template v-else>
                <h3 class="text-bold">
                  <salutation-name :contact="contact"></salutation-name>
                </h3>
              </template>
            </div>

            <!--Occupation-->
            <div class="margin-bottom" v-if="contact.occupation || contact.employer">
              {{ contact.occupation }}
              <template v-if="contact.employer">
                at {{ contact.employer }}
              </template>
            </div>

            <!--Custom Fields-->
            <custom-field-values-by-section :custom-field-values="contact.customFields" section="Below Contact Name" />
          </section>

          <!--Do Not Contact-->
          <section class="text-center" v-if="contact.do_not_contact">
            <div class="label alert">Do Not Contact</div>
          </section>

          <!--Tags-->
          <section class="text-center">
            <contact-tags :contactId="contact.id" :tags="contact.tags" @change="handleTagsUpdated"></contact-tags>
          </section>

          <!--Custom Fields-->
          <custom-field-values-by-section :custom-field-values="contact.customFields" section="Below Tags" />

          <!--Phone, Emails, and Addresses-->
          <section>
            <contact-phone-email-address :contact="contact" />
          </section>

          <!--Custom Fields-->
          <custom-field-values-by-section :custom-field-values="contact.customFields" section="Below Contact Information" />

          <!--Social Icons-->
          <section class="padding-hamburger-large text-center" v-if="contact.socials.length > 0">
            <template v-for="(social, index) in contact.socials">
              <a class="social-link" :href="social.url" target="_blank" :title="social.source" :key="index">
                <img src="/assets/social-icons/facebook.png" class="social-icon" v-if="social.source === 'Facebook'">
                <img src="/assets/social-icons/instagram.png" class="social-icon" v-if="social.source === 'Instagram'">
                <img src="/assets/social-icons/twitter.png" class="social-icon" v-if="social.source === 'Twitter'">
                <img src="/assets/social-icons/linked-in.png" class="social-icon" v-if="social.source === 'LinkedIn'">
                <img src="/assets/social-icons/website.png" class="social-icon" v-if="social.source === 'Website'">
              </a>
            </template>
          </section>

          <!--Edit Contact Link-->
          <section v-if="$route.name !== 'editContact'" class="text-center">
            <router-link :to="{ name: 'editContact', params: { id: contact.id } }">
              Edit Contact
            </router-link>
          </section>

        </div>
        <!--/.columnContentWrapper-->

      </div>
      <!--/Left Column-->

      <!--Right Column-->
      <div id="contactRightColumn" class="cell large-9 align-self-stretch">

        <!--Top Bar-->
        <div class="grid-x align-middle border-bottom bg-white rightTopBar" ref="rightTopBar">

          <!--Call Buttons (Mobile Only)-->
          <div class="mobileCallButtons cell small-12" v-if="isSmallBrowser">
            <dialer-contact-call-button
              :contact="contact"
              :contacts-queue="dialerQueue"
              :contacts-queue-context="dialerQueueContext" />
            &nbsp;&nbsp;
            <log-call-button :contact="contact" />
          </div>

          <!--Tabs Menu-->
          <ul class="cell small-12 medium-auto menu align-self-stretch contact-tab-menu">
            <li :class="{ activeTab: isActiveTab('contactOverview') }">
              <router-link :to="{ name: 'contactOverview', params: { id: contact.id } }">Overview</router-link>
            </li>
            <li :class="{ activeTab: $route.name === 'givingHistory' }" v-if="contact.is_research_feature_enabled">
              <router-link :to="{ name: 'givingHistory', params: { id: contact.id } }">Giving History</router-link>
            </li>
            <!--Show a special tab for pages that aren't part of our normal navigation bar-->
            <li class="specialTab activeTab" v-if="specialTab">
              <a>{{ specialTab }}</a>
            </li>
          </ul>

          <div class="cell shrink" v-if="!isSmallBrowser">

            <!--Call Buttons-->
            <dialer-contact-call-button
              :contact="contact"
              :contacts-queue="dialerQueue"
              :contacts-queue-context="dialerQueueContext" />
            &nbsp;&nbsp;
            <log-call-button :contact="contact" />

          </div>

        </div>
        <!--/Top Bar-->

        <!--Contents Area-->
        <div class="columnContentWrapper">
          <!--Page Contents-->
          <div class="columnContent">
            <router-view></router-view>
          </div>

          <!--Action Bar (right bar)-->
          <div id="contactActionsBar" :class="{ 'open': showActionBar, 'expanded': expandedActionBar }" v-click-outside="closeActionBar">
            <div class="scrollWrapper">
              <div class="grid-x" style="min-height: 100%">
              <div class="actionBarIcons align-self-stretch">
                <!--Add Note-->
                <a class="action-bar-icon" :class="{ 'active': isActiveAction('note') }" title="Add a Note" @click="openActionBar('note')">
                  <span class="fas fa-sticky-note"></span>
                </a>
                <!--Calls-->
                <a class="action-bar-icon" :class="{ 'active': isActiveAction('calls') }" title="Call Log" @click="openActionBar('calls')">
                  <span class="fas fa-phone"></span>
                </a>
                <!--Emails-->
                <a class="action-bar-icon" :class="{ 'active': isActiveAction('emails') }" title="Send Email" @click="openActionBar('emails')">
                  <span class="fas fa-envelope"></span>
                </a>
                <!--SMS-->
                <template>
                  <a class="action-bar-icon" :class="{ 'active': isActiveAction('sms') }" title="Send SMS" @click="openActionBar('sms')">
                    <span class="fas fa-comment"></span>
                  </a>
                </template>
                <!--Reminders-->
<!--                <template>-->
<!--                  <a class="action-bar-icon" :class="{ 'active': isActiveAction('contributions') }" title="Schedule Reminder" @click="openActionBar('reminders')">-->
<!--                    <span class="far fa-clock"></span>-->
<!--                  </a>-->
<!--                </template>-->
                <!--Contribution-->
                <template>
                  <a class="action-bar-icon" :class="{ 'active': isActiveAction('contributions') }" title="Add Pledge/Contribution" @click="openActionBar('contributions')">
                    <span class="fas fa-dollar-sign"></span>
                  </a>
                </template>
                <!--Commitment-->
                <template>
                  <a class="action-bar-icon" :class="{ 'active': isActiveAction('commitments') }" title="Add Commitment" @click="openActionBar('commitments')">
                    <span class="fas fa-hand-holding-heart"></span>
                  </a>
                </template>

                <!--Close Button-->
                <a class="action-bar-icon action-bar-close-icon" title="Close Menu" @click="closeActionBar()">
                  <span class="fas fa-arrow-right"></span>
                </a>

              </div>
              <div class="actionBarContent cell auto">
                <!--Notes-->
                <action-contact-notes
                  :contact="contact"
                  @expand="expandedActionBar = !expandedActionBar"
                  v-if="isActiveAction('note')" />
                <!--Calls-->
                <action-calls
                  :contact="contact"
                  @expand="expandedActionBar = !expandedActionBar"
                  v-if="isActiveAction('calls')" />
                <!--Emails-->
                <action-emails
                  :contact="contact"
                  v-if="isActiveAction('emails')"
                  @expand="expandedActionBar = !expandedActionBar"
                  @close="closeActionBar()" />
                <!--SMS-->
                <action-sms
                  :contact="contact"
                  v-if="isActiveAction('sms')"
                  @expand="expandedActionBar = !expandedActionBar"
                  @close="closeActionBar()" />
                <!--Contributions-->
                <action-contributions
                  :contact="contact"
                  v-if="isActiveAction('contributions')"
                  @expand="expandedActionBar = !expandedActionBar"
                  @close="closeActionBar()" />
                <!--Commitments-->
                <action-commitments
                  :contact="contact"
                  v-if="isActiveAction('commitments')"
                  @expand="expandedActionBar = !expandedActionBar"
                  @close="closeActionBar()" />
              </div>
            </div>
            </div>
          </div>

        </div>

      </div>
      <!--/Right Column-->
    </div>
    <!--/.grid-x-->

    <!--Modals-->
    <div slot="outside">
      <image-lightbox ref="lightbox" :images="lightboxImageUrls" v-if="lightboxImageUrls"></image-lightbox>
    </div>
    <!--/Modals-->
  </bleed-layout>
</template>

<script>
import BleedLayout from '../../layouts/BleedLayout.vue'
import ContactsService from '../../services/ContactsService'
import ContactTags from '../../components/Contacts/ContactTags.vue'
import ImageLightbox from '../../components/Common/ImageLightbox.vue'
import PrioritySelector from '../../components/Contacts/PrioritySelector.vue'
import ListNavigator from '../../components/Contacts/ListNavigator'
import { formsMixin } from '../../mixins/forms/formsMixin'
import { contactListQuery, contactListContactsQuery } from '../../graphql/contactLists'
import ActionContactNotes from './Actions/ActionContactNotes'
import ActionCalls from './Actions/ActionCalls'
import ActionEmails from './Actions/ActionEmails'
import ActionSms from './Actions/ActionSms'
import ActionContributions from './Actions/ActionContributions'
import SalutationName from '../../components/Contacts/SalutationName'
import ActionCommitments from './Actions/ActionCommitments'
import ContactAvatar from '../../components/Contacts/ContactAvatar'
import DialerContactCallButton from '../Dialer/DialerContactCallButton'
import LogCallButton from '../../components/LogCall/LogCallButton'
import { clickOutside } from '../../directives/clickOutsideDirective'
import CustomFieldValuesBySection from '../../components/Common/CustomFieldValuesBySection'
import ContactPhoneEmailAddress from '../../components/Contacts/View/ContactPhoneEmailAddress'
import SettingsService from '@/services/SettingsService'

export default {
  components: {
    ContactPhoneEmailAddress,
    CustomFieldValuesBySection,
    LogCallButton,
    DialerContactCallButton,
    ContactAvatar,
    ActionCommitments,
    SalutationName,
    ActionSms,
    ActionEmails,
    ActionCalls,
    ActionContributions,
    ActionContactNotes,
    ContactTags,
    PrioritySelector,
    ListNavigator,
    ImageLightbox,
    BleedLayout
  },
  directives: {
    clickOutside
  },
  data () {
    return {
      contactViewHeight: null,
      showActionBar: false,
      expandedActionBar: false,
      actionBarAction: null,
      backLinkType: null,
      backLinkRoute: null,
      listNavParams: null, // settings for the list navigator - should be set when the contact view is initially loaded
      isSmallBrowser: this.$helpers.isSmallBrowser()
    }
  },
  apollo: {
    contact: {
      ...ContactsService.getContactQuery(),
      variables () {
        return {
          // set the ID from the route as a reactive variable, so the data will
          // re-load when the url changes
          id: this.$route.params.id
        }
      },
      fetchPolicy: 'network-only'
    },
    contactList: {
      query: contactListQuery,
      variables () {
        return {
          id: this.listNavParams.listId
        }
      },
      skip () {
        return !this.listNavParams || !this.listNavParams.listId
      }
    },
    contactListContacts: {
      query: contactListContactsQuery,
      variables () {
        return {
          contactListId: this.listNavParams.listId,
          sort: this.listNavParams.sort || null,
          sortDirection: this.listNavParams.sortDirection || null
        }
      },
      skip () {
        return !this.listNavParams || !this.listNavParams.listId
      }
    }
  },
  computed: {
    lightboxImageUrls () {
      if (this.contact && this.contact.images && this.contact.images.length > 0) {
        return this.contact.images.map(image => image.full)
      }
      return null
    },
    specialTab () {
      // is this route a page that we should treat as a special tab?
      const routeName = this.$route.name
      if (routeName === 'editContact' || routeName === 'editContactImages') {
        return 'Edit'
      }
      return null
    },
    dialerQueue () {
      if (this.contactListContacts) {
        return this.contactListContacts.contacts.map(c => c.id)
      }
      return null
    },
    dialerQueueContext () {
      if (this.contactListContacts) {
        return {
          query: this.listNavParams
        }
      }
      return null
    }
  },
  methods: {
    goBack () {
      if (this.backLinkRoute) {
        this.$router.push(this.backLinkRoute)
      } else {
        console.warn('Cannot go back. There is no backLinkRoute set.')
      }
    },
    async goBackToList () {
      if (this.backLinkType === 'list') {
        await this.goBack()
      } else if (this.listNavParams) {
        await this.$router.push({
          name: 'contacts.list',
          params: {
            id: this.listNavParams.listId
          },
          query: {
            sort: this.listNavParams.sort || '',
            sortDirection: this.listNavParams.sortDirection || ''
          }
        })
      }
    },
    setBackLinkFromRoute () {
      // get the back route, if there is one
      const backRoute = this.$route.params.backRoute
      // determine the back type based on the name
      let backType = null
      if (backRoute && backRoute.name === 'contacts') {
        backType = 'search'
      }
      if (backRoute && backRoute.name === 'contacts.list') {
        backType = 'list'
      }
      // set to our variables
      this.backLinkType = backType
      this.backLinkRoute = backRoute
    },
    setListNavParamsFromRoute () {
      if (!this.$route.query || !this.$route.query.listId) {
        this.listNavParams = null
      }
      this.listNavParams = {
        listId: parseInt(this.$route.query.listId),
        sort: this.$route.query.sort,
        sortDirection: this.$route.query.sortDirection
      }
    },
    async goToContact (contactId) {
      const route = {
        name: 'contact',
        params: { id: contactId }
      }
      if (this.listNavParams) {
        route.query = this.listNavParams
      }
      await this.$router.push(route)
    },
    handleImageClick () {
      if (this.lightboxImageUrls) {
        this.$refs.lightbox.open()
      }
    },
    openActionBar (action) {
      this.showActionBar = true
      this.actionBarAction = action
    },
    closeActionBar () {
      this.showActionBar = false
      this.expandedActionBar = false
      this.actionBarAction = null
    },
    isActiveAction (action) {
      return this.actionBarAction === action
    },
    isActiveTab (routeName) {
      // for overview, we want to test for the default route, too
      if (routeName === 'contactOverview' && this.$route.name === 'contact') {
        return true
      }
      // otherwise, test the route name
      return this.$route.name === routeName
    },
    async handleUpdatePriority (priority) {
      // store the old priority
      const oldPriority = this.contact.priority
      // optimistically update the priority
      this.contact.priority = priority
      // update the priority
      try {
        await ContactsService.updateContactPriority(this.contact.id, priority)
      } catch (e) {
        this.contact.priority = oldPriority
        console.error(e)
        alert('Priority failed to update. Please try again.')
      }
    },
    handleTagsUpdated () {
      // when the tags are updated, we want to refetch the contact so the cache is updated.
      // otherwise the cache of the tags is out of sync with our contact record, so if we
      // update the tags again, it will overwrite the new tags that were just added.
      this.$apollo.queries.contact.refetch()
    },
    handleWindowResize () {
      // get the total window space, minus the top bar height
      const topBar = document.getElementsByClassName('top-bar')
      const topBarHeight = topBar[0] ? topBar[0].offsetHeight : 0
      const contactViewHeight = window.innerHeight - topBarHeight
      this.contactViewHeight = contactViewHeight
    }
  },
  watch: {
    '$route.path': function () {
      // resize window when content changes, in case it changes the window size
      this.handleWindowResize()
    },
    'contact.id': function () {
      // if the contact changes, then we should assume that this is total re-navigation.
      // we will check some settings on a total re-route, so we can preserve those settings when
      // child routes change (like changing the tab view).
      // we will check for a new back link.
      this.setBackLinkFromRoute()
      // determine if we should show the list navigator or not.
      // this should also only be checked on a total re-route (when the contact actually changes) to persist
      // the view between tab changes.
      this.setListNavParamsFromRoute()
      // update the page title
      if (this.contact) {
        const contactName = [this.contact.first_name || '', this.contact.last_name || '', this.contact.organization_name || ''].join(' ').trim()
        const campaignName = (SettingsService.getCampaignSettings() || {}).name || null
        this.$pageTitle(contactName, campaignName)
      }
    }
  },
  mounted () {
    // keep the column height full
    this.handleWindowResize()
    window.addEventListener('resize', this.handleWindowResize)
  },
  beforeDestroy () {
    // remove window listener
    window.removeEventListener('resize', this.handleWindowResize)
  },
  mixins: [
    formsMixin
  ]
}
</script>

<style lang="scss">
  @import "../../style/settings";

  .dropdown:hover .dropdown-pane {
    display: block;
    visibility: visible;
    width: initial;
    z-index: 1005
  }
  .dropdown-pane.right {
    right: 0
  }

  .viewContactComponent {
    section {
      margin-bottom: 1.5rem;
    }

    .leftTopBar,
    .rightTopBar {
      height: rem-calc(54);
      padding: 0 rem-calc(12);

      @include breakpoint (small only) {
        height: auto;
        padding: 0;
      }

      .mobileCallButtons {
        text-align: center;
        border-top: 1px solid $border-color;
        border-bottom: 1px solid $border-color;
        padding: 1em 0;
        margin-bottom: 0.5em;
      }
    }

    #contactLeftColumn {
      border-right: 1px solid $border-color;

      .columnContentWrapper {
        padding: 0.5rem 1.5rem 2rem 1.5rem;
      }

      .contactName {
        h3, h4 {
          margin-bottom: 0rem;
        }
        margin-bottom: 1.5rem;
      }

      @include breakpoint(medium down) {
        // disable the min-height values that are being set by the javascript, on smaller screens
        min-height: 0 !important;
      }
    }
    #contactRightColumn {
      padding-bottom: 2rem;

      .columnContentWrapper {
        position: relative;
        height: 100%;
        overflow: hidden;

        // contents block where the pages load
        .columnContent {
          padding: 1.7rem;
        }

        // actions bar
        $action-bar-contents-width: rem-calc(330);
        $actions-bar-icon-width: rem-calc(78); // visible width when closed
        $actions-bar-icon-width-sm: rem-calc(60);
        $action-bar-total-width: calc(#{$actions-bar-icon-width} + #{$action-bar-contents-width});
        $action-bar-total-width-sm: calc(#{$actions-bar-icon-width-sm} + #{$action-bar-contents-width});

        #contactActionsBar {
          background: $white;
          position: absolute;
          display: block;
          overflow: visible;
          right: -$action-bar-contents-width;
          top: 0;
          bottom: 0;
          width: $action-bar-total-width;
          z-index: $under-overlay-z-index;
          border-left: 1px solid $border-color;
          transition: right 280ms linear;

          @include breakpoint(small only) {
            width: $action-bar-total-width-sm;
          }

          /*
          Safari clips the overflow of "fixed" position children,
          unless the parent element is separate from the "overflow: scroll/hidden" element.
          So, adding this child component is the workaround.
           */
          .scrollWrapper {
            position: absolute;
            width: 100%;
            top: 0;
            bottom: 0;
            overflow: scroll;
          }

          &.open.expanded {
            width: 65vw;
            @include breakpoint(medium only) {
              width: 75vw;
            }
            @include breakpoint(small only) {
              width: 100vw
            }
          }

          &.open {
            right: 0;
            box-shadow: $offcanvas-shadow;

            .actionBarIcons {
              border-right: 1px solid $border-color;
            }

            .action-bar-close-icon {
              display: block !important; // show the close icon when the bar is opened
            }
          }

          .actionBarIcons {
            width: $actions-bar-icon-width;
            padding-top: 1rem;

            @include breakpoint(small only) {
              width: $actions-bar-icon-width-sm;
            }

            a.action-bar-icon {
              display: block;
              text-align: center;
              width: $actions-bar-icon-width;
              padding: .8em 0;
              font-size: rem-calc(24);
              color: $dark-gray;

              @include breakpoint(small only) {
                width: $actions-bar-icon-width-sm;
                font-size: rem-calc(22);
              }

              &:hover {
                color: $dark-font-color;
              }

              &.active {
                color: get-color(primary);
                border-right: 2px solid get-color(primary);
              }

              // close icon
              &.action-bar-close-icon {
                display: none;
                position: fixed;
                bottom: rem-calc(15);
              }
            }
          }

          .actionBarContent {
            padding: 1rem;
          }
        }

        // offset for the actions bar
        padding-right: $actions-bar-icon-width;
        @include breakpoint(small only) {
          padding-right: $actions-bar-icon-width-sm;
        }
      }
    }

    .avatar-wrapper {
      display: inline-block;
      position: relative;

      .contact-score {
        position: absolute;
        bottom: -0.6em;
        right: -0.6em;
        padding: .5em .6em;
        font-size: rem-calc(12);
      }

      margin-bottom: 0.6em; // offset the overhang from the contact score
    }

    .social-link:not(:last-child) {
      margin-right: .35em;
    }

    .backButton {
      padding-left: 0;
      color: $dark-gray !important;
    }

    .contact-tab-menu {
      li {
        display:flex;
        align-items:center; // center the child items
        position: relative;
        padding-left: .5rem;
        padding-right: .5rem;

        a {
          color: $body-font-color;
        }

        &.activeTab {
          a {
            color: get-color(primary);
          }

          &::after {
            content: "";
            display: block;
            position: absolute;
            bottom: 0;
            left: 17%;
            width: 66%;
            height: 2px;
            background-color: get-color(primary);
          }
        }

        &.specialTab {
          margin-left: 2.4rem;

          &::before {
            content: "";
            display: block;
            position: absolute;;
            left: -1.2rem;
            top: 20%;
            bottom: 20%;
            border-left: 1px solid $border-color;
          }
        }
      }
    }
  }
</style>
